import {
  gantt,
  Gantt,
  Identifiable,
  Project,
  Zone,
  zoneProjectsRetrievalFilter,
} from "@planzone/dto";
import { Repository } from "../../adapters/repository";
import { present } from "../../../utils/presenter";
import { Interactor } from "./types";

function flattenProjects(
  projectsChunks: {
    project: Identifiable<Project>;
    zone: Identifiable<Zone>;
  }[][],
): Identifiable<Project>[] {
  return projectsChunks.reduce<Identifiable<Project>[]>(
    (projects, chunk) => [
      ...projects,
      ...chunk.map(({ project, zone }) => ({ ...project, zone })),
    ],
    [],
  );
}

function flattenGantt(gantts: Gantt[]): Gantt {
  return gantts.reduce<Gantt>(
    (gantt, chunk) => ({
      ...gantt,
      constraints: [...gantt.constraints, ...chunk.constraints],
      projects: [...gantt.projects, ...chunk.projects],
    }),
    gantt,
  );
}

function removeUnPlannedProjects(
  projects: Identifiable<Project>[],
): Identifiable<Project>[] {
  return projects.filter(
    (project) => !!project.rootList.endDate && !!project.rootList.startDate,
  );
}

function createInteractor(repository: Repository): Interactor {
  return (planzone) =>
    present(
      repository
        .getZones(planzone)
        .then((zones) =>
          zones.map((zone) =>
            repository
              .getZoneProjects(zone, {
                ...zoneProjectsRetrievalFilter,
                limit: 99999999,
              })
              .then(removeUnPlannedProjects)
              .then((projects) =>
                projects.map((project) => ({
                  project,
                  zone,
                })),
              ),
          ),
        )
        .then((promises) => Promise.all(promises))
        .then(flattenProjects)
        .then((projects) =>
          projects.map((project) =>
            repository.getProjectGantt(project).then((gantt) => ({
              ...gantt,
              projects: gantt.projects.map((p) => ({ ...project, ...p })),
            })),
          ),
        )
        .then((promises) => Promise.allSettled(promises))
        .then((results) =>
          results.filter(({ status }) => status !== "rejected"),
        )
        // @ts-ignore
        .then((results) => results.map(({ value }) => value))
        .then(flattenGantt)
        .then((gantt) => ({ gantt, planzone }))
        .catch((error) => Promise.reject(planzone)),
    );
}

export type { Interactor };

export { createInteractor };
